import { useState } from 'react'
import { FaChartLine } from 'react-icons/fa'
import { FiChevronDown, FiLogOut } from 'react-icons/fi'
import {
  MdOutlineAccountBox,
  MdOutlineShop2,
  MdOutlineVerifiedUser,
  MdOutlineHistory,
  MdSettings,
} from 'react-icons/md'
import { Link, useLocation } from 'react-router-dom'

import { PersonalTrainer } from '~/components/Icons/PersonalTrainer'
import { authenticationAtom } from '~/modules/authentication/data'
import { signOut } from '~/modules/authentication/services'
import { AdminUser } from '~/modules/authentication/types'
import { useAtom } from '~/observers/jotai'
import { adminRoutes } from '~/routes/routes'

import gpaLogo from '../../../../assets/img/gpaLogo.png'
import { MenuItem } from './MenuItem'

export const SideMenu: React.FC = () => {
  const [session] = useAtom(authenticationAtom)
  const { pathname } = useLocation()

  const inSales =
    pathname.includes(adminRoutes.sales.root) ||
    pathname.includes(adminRoutes.licenses.root)
  const [salesOpen, setSalesOpen] = useState(inSales)

  return (
    <div
      className='flex flex-col flex-1
      flex-shrink-0 w-full max-w-[300px] border-r-2 border-r-slate-200'
    >
      <div className='flex items-center justify-center pb-2 pt-4 border-b-2'>
        <img src={gpaLogo} width='35%' />
      </div>

      <ul className='flex flex-col py-4 h-full'>
        <li>
          <Link to={'/admin/painel'}>
            <MenuItem
              active={'/admin/painel' === pathname}
              title='Painel'
              icon={<FaChartLine size={24} />}
            />
          </Link>
        </li>

        <li>
          <Link to={adminRoutes.personalTrainers()}>
            <MenuItem
              active={pathname.includes(adminRoutes.personalTrainers())}
              title='Personal Trainers'
              icon={<PersonalTrainer size={24} />}
            />
          </Link>
        </li>

        {/* Vendas — collapsible with Modelos and Licenças submenus */}
        <li>
          <button
            onClick={() => setSalesOpen((o) => !o)}
            data-active={inSales}
            className='relative flex gap-2 items-center text-black
              hover:text-gpa-blue-500 data-[active=true]:text-gpa-blue-500
              hover:bg-blue-50 data-[active=true]:bg-blue-50 mx-3 px-3 py-2
              rounded-md w-[calc(100%-1.5rem)]'
          >
            <span
              data-active={inSales}
              className='absolute top-1/2 -translate-y-1/2 left-0 scale-0
              data-[active=true]:scale-100 w-[3px] bg-gpa-blue-500 h-4
              rounded-tr-sm rounded-br-sm'
            />
            <div className='shrink-0'><MdOutlineShop2 size={24} /></div>
            <span className='flex-1 text-left'>Vendas</span>
            <FiChevronDown
              size={16}
              className={`shrink-0 transition-transform duration-200 ${salesOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {salesOpen && (
            <ul className='flex flex-col ml-6 mt-1 border-l-2 border-slate-200 pl-2'>
              <li>
                <Link
                  to={adminRoutes.sales.root}
                  data-active={pathname.includes('/admin/vendas')}
                  className='flex px-3 py-1.5 text-sm rounded-md text-slate-600
                    hover:text-gpa-blue-500 hover:bg-blue-50
                    data-[active=true]:text-gpa-blue-500 data-[active=true]:bg-blue-50
                    data-[active=true]:font-medium'
                >
                  Modelos
                </Link>
              </li>
              <li>
                <Link
                  to={adminRoutes.licenses.root}
                  data-active={pathname.includes('/admin/licencas')}
                  className='flex px-3 py-1.5 text-sm rounded-md text-slate-600
                    hover:text-gpa-blue-500 hover:bg-blue-50
                    data-[active=true]:text-gpa-blue-500 data-[active=true]:bg-blue-50
                    data-[active=true]:font-medium'
                >
                  Licenças
                </Link>
              </li>
            </ul>
          )}
        </li>

        {(session?.user as AdminUser).role === 'owner' ? (
          <li className='mt-'>
            <Link to={adminRoutes.admins}>
              <MenuItem
                active={pathname.includes(adminRoutes.admins)}
                title='Administradores'
                icon={<MdOutlineVerifiedUser size={24} />}
              />
            </Link>
          </li>
        ) : null}

        <li className='mt-2'>
          <Link
            data-active={pathname.includes(adminRoutes.actions)}
            to={adminRoutes.actions}
            className='relative flex gap-2 items-center text-black hover:text-gpa-blue-500 data-[active=true]:text-gpa-blue-500 hover:bg-blue-50 data-[active=true]:bg-blue-50 mx-3 px-3 py-2 rounded-md'
          >
            <span
              data-active={pathname.includes(adminRoutes.actions)}
              className='absolute top-1/2 -translate-y-1/2 left-0 scale-0 data-[active=true]:scale-100 w-[3px] bg-gpa-blue-500 h-4 rounded-tr-sm rounded-br-sm'
            ></span>
            <div className='shrink-0'>
              <MdOutlineHistory size={24} />
            </div>
            <span>Ações</span>
          </Link>
        </li>

        <li className='mt-2'>
          <Link
            data-active={pathname.includes(adminRoutes.profile)}
            to={adminRoutes.profile}
            className='relative flex gap-2 items-center text-black hover:text-gpa-blue-500 data-[active=true]:text-gpa-blue-500 hover:bg-blue-50 data-[active=true]:bg-blue-50 mx-3 px-3 py-2 rounded-md'
          >
            <span
              data-active={pathname.includes(adminRoutes.profile)}
              className='absolute top-1/2 -translate-y-1/2 left-0 scale-0 data-[active=true]:scale-100 w-[3px] bg-gpa-blue-500 h-4 rounded-tr-sm rounded-br-sm'
            ></span>
            <div className='shrink-0'>
              <MdOutlineAccountBox size={24} />
            </div>
            <span>Minha Conta</span>
          </Link>
        </li>

        <li className='mt-2'>
          <Link to={adminRoutes.settings}>
            <MenuItem
              active={pathname.includes(adminRoutes.settings)}
              title='Configurações'
              icon={<MdSettings size={24} />}
            />
          </Link>
        </li>

        <li className='flex mt-auto border-t-2 border-t-slate-300 pt-3 mx-3'>
          <button
            className='flex w-full justify-center gap-2 items-center text-black hover:text-red-500 hover:bg-red-100  px-3 py-2 rounded-md'
            onClick={signOut}
          >
            <span className='absolute -top-5 left-0 right-0 h-0.5 bg-slate-300 pointer-events-none'></span>
            <span>Sair</span>
            <div className='shrink-0'>
              <FiLogOut size={24} />
            </div>
          </button>
        </li>
      </ul>
    </div>
  )
}
